import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, DatePicker, Descriptions, Input, InputNumber, Modal, Radio, Segmented,
  Select, Space, Steps, Tag, Upload, message,
} from 'antd';
import { PaperClipOutlined, UploadOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../utils/config';
import { formatMoney, inputNumberMoneyProps } from '../../utils/currency';
import { authHeaders } from '../../utils/auth';
import { useAuth } from '../../context/AuthContext';

/**
 * Tek bir enerji adımını işleyen modal.
 *
 * Tek ekranda (EnerjiSurecPage) bir adım sütunundaki Tag'e tıklanınca açılır.
 * Durum listesi ve zorunlu alanlar backend tanımından (`GET /enerji/tanim`)
 * gelir — akış değişince burada kod değişmez.
 */
const EnerjiAdimModal = ({ open, satir, adimAnahtari, tanim, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [durum, setDurum] = useState(null);
  const [tarih, setTarih] = useState(null);
  const [alanDegerleri, setAlanDegerleri] = useState({});
  const [kaydediyor, setKaydediyor] = useState(false);
  // Red, ayrı bir "detay" modalı yerine adımın kendi içinde işlenir: hangi
  // adımda takılındığı zaten burada belli.
  const [redAcik, setRedAcik] = useState(false);
  const [redSebep, setRedSebep] = useState(null);
  const [redAciklama, setRedAciklama] = useState('');
  // Ödeme onayı reddi (20.08 madde 11): 'beklet' ya da 'iptal' + açıklama
  const [onayRedAcik, setOnayRedAcik] = useState(false);
  const [onayRedKarar, setOnayRedKarar] = useState('beklet');
  const [onayRedAciklama, setOnayRedAciklama] = useState('');
  // Ruhsat dosyası (20.08 madde 1)
  const [dosyaYukleniyor, setDosyaYukleniyor] = useState(false);
  const [dosyaBilgi, setDosyaBilgi] = useState(null); // {dosya_adi, var}

  const adim = useMemo(
    () => (tanim?.adimlar || []).find((a) => a.anahtar === adimAnahtari) || null,
    [tanim, adimAnahtari]
  );

  const hucre = satir?.adim_durumlari?.[adimAnahtari];

  // Çoklu abone girişi (13.08): lokasyondaki kurulu kabin sayısı Select
  // seçeneklerini belirler; mevcut abone kayıtları forma ön-doldurulur.
  const [kabinSayisi, setKabinSayisi] = useState(0);

  useEffect(() => {
    if (!open) return;
    setDurum(hucre?.alt_durum || null);
    setTarih(hucre?.tarih ? dayjs(hucre.tarih) : null);
    setAlanDegerleri({});
    setRedAcik(false);
    setRedSebep(null);
    setRedAciklama('');
    setOnayRedAcik(false);
    setOnayRedKarar('beklet');
    setOnayRedAciklama('');
    setDosyaBilgi(hucre?.dosya || null);
    // Geç gelen yanıt başka bir lokasyonun modalını doldurmasın (iptal bayrağı).
    let iptal = false;
    if (satir?.lokasyon) {
      axios
        .get(`${API_BASE_URL}/enerji/${satir.lokasyon}`, authHeaders())
        .then((r) => {
          if (iptal || String(r.data?.lokasyon) !== String(satir.lokasyon)) return;
          setKabinSayisi(Number(r.data?.kabin_sayisi) || 0);
          if (Array.isArray(r.data?.aboneler) && r.data.aboneler.length) {
            setAlanDegerleri((s) => ({ ...s, aboneler: r.data.aboneler }));
          }
          const k = r.data?.kayit;
          if (k) setDosyaBilgi({ var: Boolean(k.ruhsat_gorsel), dosya_adi: k.ruhsat_dosya_adi });
        })
        .catch(() => { if (!iptal) setKabinSayisi(0); });
    }
    return () => { iptal = true; };
  }, [open, adimAnahtari, hucre?.alt_durum, hucre?.tarih, hucre?.dosya, satir?.lokasyon]);

  // Seçilen duruma bağlı zorunlu/opsiyonel alanlar (muayene tarihi, abone no,
  // ruhsat bedel kalemleri ...).
  const gerekliAlanlar = useMemo(() => {
    if (!adim) return [];
    const durumAlanlari = (adim.durum_alanlari || {})[durum] || [];
    const onayFormu = adim.onay_formu;
    const onayAlanlari =
      onayFormu && durum === onayFormu.tetikleyen_durum ? onayFormu.alanlar : [];
    return [...durumAlanlari, ...onayAlanlari];
  }, [adim, durum]);

  const kaydet = async () => {
    if (!durum) {
      message.warning('Durum seçin.');
      return;
    }
    const eksik = gerekliAlanlar.find((a) => {
      if (!a.zorunlu) return false;
      if (a.tip === 'abone_listesi') {
        return !(alanDegerleri[a.ad] || []).some((r) => (r?.abone_no || '').trim());
      }
      const v = alanDegerleri[a.ad];
      // 0 geçerli bir tutardır (ör. teminatsız ruhsat) — yalnızca boş/null eksik sayılır
      return v === null || v === undefined || v === '';
    });
    if (eksik) {
      message.warning(`'${eksik.etiket}' alanı zorunlu.`);
      return;
    }
    setKaydediyor(true);
    try {
      await axios.post(
        `${API_BASE_URL}/enerji/${satir.lokasyon}/adim`,
        {
          adim: adimAnahtari,
          durum,
          tarih: tarih ? tarih.format('YYYY-MM-DD') : null,
          alan_degerleri: alanDegerleri,
        },
        authHeaders()
      );
      message.success(`${adim?.ad || 'Adım'} güncellendi.`);
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Kaydedilemedi.');
    } finally {
      setKaydediyor(false);
    }
  };

  // Onay bekleyen adım: tutar formu yerine "onay bekliyor" özeti gösterilir.
  // 20.08 madde 11: onay SIRALI kademelerden geçer (Anıl > Murat); sıradaki
  // kademenin onaycıları backend tanımından (kademe_onaycilari) gelir.
  const onayBilgi = hucre?.onay;
  const onayBekliyor = Boolean(onayBilgi?.bekliyor);
  const kademeler = onayBilgi?.kademeler || [];
  const siradaki = kademeler.find((k) => k.sirada) || null;
  const kademeOnaycilari = tanim?.kademe_onaycilari || {};
  const onaycilar = siradaki
    ? (kademeOnaycilari[siradaki.anahtar] || [])
    : ((tanim?.onaycilar || {})[onayBilgi?.rol] || []);
  const kullaniciNo = user?.kullanici_no ?? null;
  const onaylayabilirim = onaycilar.some((o) => o.kullanici_no === kullaniciNo);
  const [onayIsliyor, setOnayIsliyor] = useState(false);
  const kisiAdi = (no) =>
    (tanim?.kisiler || []).find((k) => k.kullanici_no === no)?.ad_soyad || (no ? `#${no}` : '-');

  const onayVer = async (onay) => {
    if (!onay) {
      if (!onayRedAciklama.trim()) { message.warning('Red için açıklama zorunludur.'); return; }
    }
    setOnayIsliyor(true);
    try {
      await axios.post(
        `${API_BASE_URL}/enerji/${satir.lokasyon}/ruhsat-onay`,
        onay
          ? { onay: true, aciklama: null }
          : { onay: false, karar: onayRedKarar, aciklama: onayRedAciklama.trim() },
        authHeaders()
      );
      message.success(onay
        ? 'Onaylandı'
        : (onayRedKarar === 'iptal' ? 'Reddedildi — süreç iptal edildi' : 'Reddedildi — süreç beklemeye alındı'));
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setOnayIsliyor(false);
    }
  };

  // Ruhsat dosyası yükle / aç (20.08 madde 1)
  const dosyaYukle = async (file) => {
    const fd = new FormData();
    fd.append('dosya', file);
    setDosyaYukleniyor(true);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/enerji/${satir.lokasyon}/ruhsat-gorsel`, fd,
        { ...authHeaders(), headers: { ...(authHeaders().headers || {}), 'Content-Type': 'multipart/form-data' } }
      );
      setDosyaBilgi({ var: true, dosya_adi: r.data?.dosya_adi || file.name });
      message.success('Ruhsat dosyası yüklendi');
      onSuccess && onSuccess();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Dosya yüklenemedi');
    } finally {
      setDosyaYukleniyor(false);
    }
    return false;
  };
  const dosyaAc = async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/enerji/${satir.lokasyon}/ruhsat-gorsel`, authHeaders());
      if (r.data?.url) window.open(r.data.url, '_blank', 'noopener');
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Dosya açılamadı');
    }
  };

  const paraMetni = (v) => formatMoney(v);

  const redSebepleri = tanim?.red_sebepleri || [];
  const seciliRed = redSebepleri.find((r) => r.deger === redSebep);

  const reddet = async () => {
    if (!redSebep) {
      message.warning('Red sebebi seçin.');
      return;
    }
    if (!redAciklama.trim()) {
      message.warning('Açıklama zorunludur.');
      return;
    }
    setKaydediyor(true);
    try {
      await axios.post(
        `${API_BASE_URL}/enerji/${satir.lokasyon}/red`,
        { red_sebebi: redSebep, aciklama: redAciklama.trim() },
        authHeaders()
      );
      message.success('Red kaydedildi.');
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Red kaydedilemedi.');
    } finally {
      setKaydediyor(false);
    }
  };

  const kapsamDisi = hucre?.durum === 'kapsam_disi';
  // 20.08 madde 8: reddedilmiş/iptal edilmiş süreçte adım işlenemez.
  const surecKapali = Boolean(satir?.reddedildi) || ['reddedildi', 'iptal'].includes(satir?.surec_durumu);
  const kilitli = kapsamDisi || surecKapali;
  const firma = hucre?.firma;

  return (
    <Modal
      title={`${adim?.ad || 'Adım'} — ${satir?.lokasyon_adi || satir?.lokasyon || ''}`}
      open={open}
      onCancel={onClose}
      onOk={kaydet}
      okText="Kaydet"
      okButtonProps={{ loading: kaydediyor, disabled: kilitli || onayBekliyor }}
      cancelText="Vazgeç"
      destroyOnHidden
      width={560}
    >
      {kapsamDisi && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Bu adım görev kabulünde kapsam dışı bırakılmış."
          description="İşlemek için önce görev kabulünden bu adımı seçili hale getirin."
        />
      )}

      {surecKapali && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={satir?.surec_durumu === 'iptal' ? 'Süreç iptal edildi' : 'Süreç reddedildi'}
          description={`${satir?.red_sebebi || ''}${satir?.red_aciklama ? ` — ${satir.red_aciklama}` : ''}. Adımlar kilitli; yeniden açma yalnızca yönetim kararıyla.`}
        />
      )}

      {satir?.beklemede && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Süreç beklemede"
          description={`${satir.bekleme_sebebi || ''} — ${satir.bekleme_aciklama || ''}${satir.bekleme_sorumlu_adi ? ` (görevli: ${satir.bekleme_sorumlu_adi})` : ''}`}
        />
      )}

      <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="Termin">{adim?.termin_gun ?? '-'} gün</Descriptions.Item>
        <Descriptions.Item label="Mevcut Durum">
          {(adim?.durumlar || []).find((d) => d.deger === hucre?.alt_durum)?.etiket || hucre?.alt_durum || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Firma" span={2}>
          {firma?.firma_adi
            ? <Tag color="geekblue">{firma.firma_adi}</Tag>
            : <span style={{ color: '#8c8c8c' }}>Seçilmedi (Görev Kabul'den atanır)</span>}
        </Descriptions.Item>
      </Descriptions>

      {adim?.gorsel_eki && (
        <div style={{ marginBottom: 12, padding: 8, border: '1px dashed #d9d9d9', borderRadius: 6 }}>
          <div style={{ fontSize: 12, marginBottom: 6 }}>Ruhsat Dosyası (PDF / JPG / PNG)</div>
          <Space wrap>
            {dosyaBilgi?.var ? (
              <Button size="small" icon={<PaperClipOutlined />} onClick={dosyaAc}>
                {dosyaBilgi.dosya_adi || 'Dosyayı aç'}
              </Button>
            ) : (
              <span style={{ color: '#8c8c8c', fontSize: 12 }}>Henüz yüklenmedi</span>
            )}
            <Upload
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              showUploadList={false}
              beforeUpload={dosyaYukle}
              disabled={kilitli || dosyaYukleniyor}
            >
              <Button size="small" icon={<UploadOutlined />} loading={dosyaYukleniyor} disabled={kilitli}>
                {dosyaBilgi?.var ? 'Değiştir' : 'Yükle'}
              </Button>
            </Upload>
          </Space>
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, marginBottom: 4 }}>Durum</div>
        <Segmented
          block
          options={(adim?.durumlar || tanim?.genel_durumlar || []).map((d) => ({
            label: d.etiket,
            value: d.deger,
            // Onay beklenirken adım kapatılamaz; backend de 409 döner.
            disabled: onayBekliyor && ['tamamlandi', 'yapildi'].includes(d.deger),
          }))}
          value={durum}
          onChange={setDurum}
          disabled={kilitli}
        />
      </div>

      {onayBekliyor && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={siradaki
            ? `Ödeme onayı bekleniyor — sırada: ${siradaki.etiket}`
            : `Yönetim onayı bekleniyor (${onaycilar.length} kişi)`}
          description={
            <div style={{ fontSize: 12 }}>
              <div style={{ marginBottom: 6 }}>
                Tutarlar gönderildi. Onaylar sırayla verilir; tüm onaylar gelmeden bu adım
                kapatılamaz ve sonraki adımlara geçilemez.
              </div>
              <Descriptions size="small" column={1} bordered style={{ marginBottom: 8 }}>
                <Descriptions.Item label="Toplam Tutar">
                  {paraMetni(onayBilgi?.gonderilen?.ruhsat_toplam_tutar)}
                </Descriptions.Item>
                <Descriptions.Item label="Harç Bedeli">
                  {paraMetni(onayBilgi?.gonderilen?.ruhsat_harc_bedeli)}
                </Descriptions.Item>
                <Descriptions.Item label="Teminat Bedeli">
                  {paraMetni(onayBilgi?.gonderilen?.ruhsat_teminat_bedeli)}
                </Descriptions.Item>
              </Descriptions>
              {kademeler.length > 0 && (
                <Steps
                  size="small"
                  style={{ marginBottom: 8 }}
                  current={Math.max(0, kademeler.findIndex((k) => k.sirada))}
                  items={kademeler.map((k) => ({
                    title: k.etiket,
                    status: k.onaylandi ? 'finish' : (k.sirada ? 'process' : 'wait'),
                    description: k.onaylandi
                      ? `${kisiAdi(k.onaylayan)}${k.onay_tarihi ? ` · ${dayjs(k.onay_tarihi).format('DD.MM.YYYY HH:mm')}` : ''}`
                      : (kademeOnaycilari[k.anahtar] || []).map((o) => o.ad_soyad).join(', ') || k.rol,
                  }))}
                />
              )}
              <div>
                Sıradaki onaycı:{' '}
                {onaycilar.length
                  ? onaycilar.map((o) => (
                      <Tag key={o.kullanici_no}>{o.ad_soyad}</Tag>
                    ))
                  : <Tag>Onaycı tanımlı değil</Tag>}
              </div>
              {onaylayabilirim && !onayRedAcik && (
                <Space style={{ marginTop: 10 }}>
                  <Button type="primary" loading={onayIsliyor} onClick={() => onayVer(true)}>
                    Onayla
                  </Button>
                  <Button danger loading={onayIsliyor} onClick={() => setOnayRedAcik(true)}>
                    Reddet
                  </Button>
                </Space>
              )}
              {onaylayabilirim && onayRedAcik && (
                <div style={{ marginTop: 10, padding: 8, background: 'rgba(255,77,79,0.06)', borderRadius: 6 }}>
                  <div style={{ fontWeight: 600, color: '#cf1322', marginBottom: 6 }}>Red kararı</div>
                  <Radio.Group
                    value={onayRedKarar}
                    onChange={(e) => setOnayRedKarar(e.target.value)}
                    style={{ marginBottom: 8 }}
                    options={[
                      { value: 'beklet', label: 'Beklemeye al (tutar revize edilip yeniden onaya gönderilir)' },
                      { value: 'iptal', label: 'Süreci tamamen iptal et' },
                    ]}
                  />
                  <Input.TextArea
                    rows={2}
                    placeholder="Açıklama (zorunlu)"
                    value={onayRedAciklama}
                    onChange={(e) => setOnayRedAciklama(e.target.value)}
                  />
                  <Space style={{ marginTop: 8 }}>
                    <Button danger type="primary" loading={onayIsliyor} onClick={() => onayVer(false)}>
                      {onayRedKarar === 'iptal' ? 'Reddet ve İptal Et' : 'Reddet ve Beklet'}
                    </Button>
                    <Button type="text" onClick={() => setOnayRedAcik(false)}>Vazgeç</Button>
                  </Space>
                </div>
              )}
            </div>
          }
        />
      )}

      {onayBilgi?.onaylandi && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message="Ödeme onayı verildi (tüm kademeler)"
          description={
            <div style={{ fontSize: 12 }}>
              <div>Toplam tutar: {paraMetni(onayBilgi?.gonderilen?.ruhsat_toplam_tutar)}</div>
              {kademeler.map((k) => (
                <div key={k.anahtar}>
                  {k.etiket}: {kisiAdi(k.onaylayan)}
                  {k.onay_tarihi ? ` · ${dayjs(k.onay_tarihi).format('DD.MM.YYYY HH:mm')}` : ''}
                </div>
              ))}
              <div style={{ marginTop: 4, opacity: 0.8 }}>
                Ödeme yapılınca durumu "Tamamlandı" yapın; bekleme kendiliğinden biter.
              </div>
            </div>
          }
        />
      )}
      {onayBilgi?.red_karar === 'beklet' && !onayBekliyor && !onayBilgi?.onaylandi && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Ödeme onayı reddedildi — tutarlar revize edilip yeniden gönderilmeli"
        />
      )}

      {!onayBekliyor && gerekliAlanlar.map((alan) => (
        <div key={alan.ad} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}>
            {alan.etiket}
            {alan.zorunlu && <span style={{ color: 'red' }}> *</span>}
          </div>
          {alan.tip === 'abone_listesi' ? (
            <AboneListesiAlani
              value={alanDegerleri[alan.ad] || []}
              kabinSayisi={kabinSayisi}
              onChange={(v) => setAlanDegerleri((s) => ({ ...s, [alan.ad]: v }))}
            />
          ) : alan.tip === 'tarih' ? (
            <DatePicker
              style={{ width: '100%' }}
              format="DD.MM.YYYY"
              value={alanDegerleri[alan.ad] ? dayjs(alanDegerleri[alan.ad]) : null}
              onChange={(d) =>
                setAlanDegerleri((s) => ({ ...s, [alan.ad]: d ? d.format('YYYY-MM-DD') : null }))
              }
            />
          ) : alan.tip === 'para' ? (
            <InputNumber
              {...inputNumberMoneyProps}
              style={{ width: '100%' }}
              min={0}
              value={alanDegerleri[alan.ad]}
              onChange={(v) => setAlanDegerleri((s) => ({ ...s, [alan.ad]: v }))}
            />
          ) : (
            <Input
              value={alanDegerleri[alan.ad] || ''}
              onChange={(e) => setAlanDegerleri((s) => ({ ...s, [alan.ad]: e.target.value }))}
            />
          )}
        </div>
      ))}

      <div>
        <div style={{ fontSize: 12, marginBottom: 4 }}>İşlem Tarihi (opsiyonel)</div>
        <DatePicker
          style={{ width: 220 }}
          format="DD.MM.YYYY"
          value={tarih}
          onChange={setTarih}
          disabled={kilitli}
        />
      </div>

      {/* Red — 20.08: süreç TAMAMEN durur, önceki adımlara dönülmez. */}
      {!kilitli && (
        <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
          {!redAcik ? (
            <Button danger type="text" size="small" onClick={() => setRedAcik(true)}>
              Bu adımda reddedildi
            </Button>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#cf1322' }}>Red</div>
              <Select
                style={{ width: '100%' }}
                placeholder="Red sebebi"
                value={redSebep}
                onChange={setRedSebep}
                options={redSebepleri.map((r) => ({ value: r.deger, label: r.etiket }))}
              />
              <Alert
                type="warning"
                showIcon
                message={tanim?.red_sonucu || 'Süreç tamamen durur; önceki adımlara dönülmez.'}
                description={seciliRed?.aciklama && seciliRed.aciklama !== tanim?.red_sonucu ? seciliRed.aciklama : null}
              />
              <Input.TextArea
                rows={2}
                placeholder="Açıklama (zorunlu)"
                value={redAciklama}
                onChange={(e) => setRedAciklama(e.target.value)}
              />
              <Space>
                <Button danger loading={kaydediyor} onClick={reddet}>Reddet</Button>
                <Button type="text" onClick={() => setRedAcik(false)}>Vazgeç</Button>
              </Space>
            </Space>
          )}
        </div>
      )}
    </Modal>
  );
};

/**
 * Kabin başına abone numarası girişi (13.08 kararı: abone no çoklu olacak,
 * kabinler için ayrı girilecek). Kabin seçenekleri lokasyondaki kurulu kabin
 * sayısından (GET /enerji/{lokasyon} → kabin_sayisi) gelir; 0 dönerse 16'ya
 * kadar serbest bırakılır (eski/eksik envanter kayıtları kilitlemesin).
 */
export const AboneListesiAlani = ({ value, kabinSayisi, onChange }) => {
  const satirlar = value.length ? value : [{ kabin_no: 1, abone_no: '', sayac_no: '' }];
  const maxKabin = Math.min(Math.max(kabinSayisi || 16, 1), 16);
  const kullanilan = satirlar.map((r) => r.kabin_no);
  const set = (i, patch) =>
    onChange(satirlar.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const ekle = () => {
    const bos = Array.from({ length: maxKabin }, (_, k) => k + 1)
      .find((n) => !kullanilan.includes(n));
    onChange([...satirlar, { kabin_no: bos || satirlar.length + 1, abone_no: '', sayac_no: '' }]);
  };
  const sil = (i) => onChange(satirlar.filter((_, j) => j !== i));
  return (
    <div>
      {satirlar.map((r, i) => (
        <Space key={i} style={{ marginBottom: 6, display: 'flex' }} align="center">
          <Select
            style={{ width: 110 }}
            value={r.kabin_no}
            options={Array.from({ length: maxKabin }, (_, k) => ({
              value: k + 1,
              label: `Kabin ${k + 1}`,
              disabled: kullanilan.includes(k + 1) && r.kabin_no !== k + 1,
            }))}
            onChange={(v) => set(i, { kabin_no: v })}
          />
          <Input
            style={{ width: 170 }}
            placeholder="Abone no *"
            maxLength={50}
            value={r.abone_no}
            onChange={(e) => set(i, { abone_no: e.target.value })}
          />
          <Input
            style={{ width: 150 }}
            placeholder="Sayaç no"
            maxLength={50}
            value={r.sayac_no || ''}
            onChange={(e) => set(i, { sayac_no: e.target.value })}
          />
          {satirlar.length > 1 && (
            <Button size="small" danger type="text" onClick={() => sil(i)}>Sil</Button>
          )}
        </Space>
      ))}
      {satirlar.length < maxKabin && (
        <Button size="small" onClick={ekle}>+ Kabin Ekle</Button>
      )}
      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
        {kabinSayisi > 0 ? `Bu lokasyonda kurulu kabin sayısı: ${kabinSayisi}. ` : ''}
        Abone ve sayaç numaraları serbest metindir (harf/boşluk kullanılabilir).
      </div>
    </div>
  );
};

export default EnerjiAdimModal;
